import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Table } from "react-bootstrap";
import "./simpleTable.css";
import SortIcon from "./SortIcon.png";

const normalizeColumns = (columns) =>
  columns.map((col) => {
    if (typeof col === "string") {
      return { header: col, accessor: col };
    }
    // Expect object with at least accessor and optional header
    return {
      header: col.header ?? col.accessor,
      accessor: col.accessor,
      ...col, // allow extra props if needed
    };
  });

const compareValues = (a, b, direction) => {
  // Handle null/undefined
  if (a == null && b == null) return 0;
  if (a == null) return direction === "asc" ? -1 : 1;
  if (b == null) return direction === "asc" ? 1 : -1;

  // Strings: localeCompare; others: fallback to default
  if (typeof a === "string" && typeof b === "string") {
    const res = a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return direction === "asc" ? res : -res;
  }
  if (a < b) return direction === "asc" ? -1 : 1;
  if (a > b) return direction === "asc" ? 1 : -1;
  return 0;
};

const SimpleTable = ({
  title,
  columns = [],
  data = [],
  headerActions = null,
  scrollable = false,
  scrollHeight = "300px",
  customRowRender,
  tableClassName,
  showPagination = true,
  renderPagination, // Custom pagination renderer from parent
  paginationData = {},
  fetchData,
  searchValue,
  isSubTable = false,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const {
    totalPages = 0,
    totalRecords = 0,
    pageNumber = 1,
    pageSize = 25,
  } = paginationData;

  const normalizedCols = useMemo(() => normalizeColumns(columns), [columns]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // helper to support nested accessors like 'user.name'
  const getValueByAccessor = useCallback((row, accessor) => {
    if (!accessor) return undefined;
    if (!row) return undefined;
    if (typeof accessor !== "string") return row[accessor];
    return accessor.split(".").reduce((obj, key) => (obj ? obj[key] : undefined), row);
  }, []);

  const handleSort = useCallback((accessor) => {
    if (!accessor) return;
    setSortConfig((prev) => {
      if (prev.key === accessor) {
        return { key: accessor, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key: accessor, direction: "asc" };
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return [...data];
    const copy = [...data];
    copy.sort((a, b) =>
      compareValues(
        getValueByAccessor(a, sortConfig.key),
        getValueByAccessor(b, sortConfig.key),
        sortConfig.direction
      )
    );
    return copy;
  }, [data, sortConfig]);

  const handleOnPaginationChange = (newPage, pageSize) => {
    fetchData(searchValue, newPage, pageSize);
  };

  const from = data.length === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const to =
    pageNumber * pageSize > data.length
      ? from + data.length - 1
      : pageNumber * pageSize;

  // Default pagination renderer
  const defaultPaginationRenderer = () => (
    <div
      className="d-flex align-items-center gap-4  justify-content-between"
      style={{
        borderTop: "1px solid #e9ecef",
        paddingTop: "16px",
        fontSize: "14px",
        width: "100%",
      }}
    >
      {/* Items per page */}
      <div className="d-flex align-items-center gap-2">
        <span>Items per page</span>
        <select
          className="form-select form-select-sm"
          value={pageSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            if (onRowsPerPageChange) {
              onRowsPerPageChange(newSize);
            } else {
              handleOnPaginationChange(1, newSize);
            }
          }}
          style={{ width: "70px", fontSize: "14px" }}
          title="Items per page"
        >
          {[25, 50, 75, 100].map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
      </div>
      <div className="d-flex align-items-center gap-4">
        {/* Count */}
        <div style={{ color: "#6c757d", fontWeight: 500 }}>
          {from}-{to} of {totalRecords}
        </div>

        {/* Navigation */}
        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-lg btn-outline-secondary border-0"
            style={{ width: "32px", height: "32px", padding: 0 }}
            disabled={pageNumber === 1}
            onClick={() => onPageChange ? onPageChange(1) : handleOnPaginationChange(1, pageSize)}
            title="First page"
            aria-label="First page"
          >
            ≪
          </button>
          <button
            className="btn btn-lg btn-outline-secondary border-0"
            style={{ width: "32px", height: "32px", padding: 0 }}
            disabled={pageNumber === 1}
            onClick={() => onPageChange ? onPageChange(pageNumber - 1) : handleOnPaginationChange(pageNumber - 1, pageSize)}
            title="Previous page"
            aria-label="Previous page"
          >
            ‹
          </button>
          <span
            style={{
              minWidth: "32px",
              textAlign: "center",
              fontWeight: 600,
              padding: "0 8px",
            }}
          >
            {pageNumber}
          </span>
          <button
            className="btn btn-lg btn-outline-secondary border-0"
            style={{ width: "32px", height: "32px", padding: 0 }}
            disabled={pageNumber === totalPages}
            onClick={() => onPageChange ? onPageChange(pageNumber + 1) : handleOnPaginationChange(pageNumber + 1, pageSize)}
            title="Next page"
            aria-label="Next page"
          >
            ›
          </button>
          <button
            className="btn btn-lg btn-outline-secondary border-0"
            style={{ width: "32px", height: "32px", padding: 0 }}
            disabled={pageNumber === totalPages}
            onClick={() => onPageChange ? onPageChange(totalPages) : handleOnPaginationChange(totalPages, pageSize)}
            title="Last page"
            aria-label="Last page"
          >
            ≫
          </button>
        </div>
      </div>
    </div>
  );

  const paginationComponent = () => {
    if (!showPagination || scrollable) return null;
    return renderPagination ? renderPagination() : defaultPaginationRenderer();
  };

  // Helper for aria-sort value
  const getAriaSort = (colAccessor) => {
    if (sortConfig.key !== colAccessor) return "none";
    return sortConfig.direction === "asc" ? "ascending" : "descending";
  };

  return (
    <div className="p-3 border rounded bg-white">
      {title && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">{title}</h6>
          {headerActions && <div>{headerActions}</div>}
        </div>
      )}

      <div
        className={`table-responsive ${scrollable ? "overflow-auto" : ""}`}
        style={scrollable ? { maxHeight: scrollHeight } : undefined}
      >
        <Table
          className={`mb-0 rounded-table ${tableClassName || ""}`}
          bordered
          hover
          size="sm"
        >
          <thead
            className="table-primary"
            style={{ backgroundColor: "#586C8F" }}
          >
            <tr>
              {normalizedCols.map((col, idx) => {
                const sortable = !col.disableSort && col.accessor;
                return (
                  <th
                    key={idx}
                    role={sortable ? "button" : undefined}
                    tabIndex={sortable ? 0 : undefined}
                    aria-sort={sortable ? getAriaSort(col.accessor) : undefined}
                    onClick={() => sortable && handleSort(col.accessor)}
                    onKeyDown={(e) => {
                      if (!sortable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSort(col.accessor);
                      }
                    }}
                    style={{
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      textAlign: col.align || "center",
                    }}
                    className={`
                      ${idx === 0 ? "first-col" : ""}
                      ${idx === normalizedCols.length - 1 ? "last-col" : ""}
                    `}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {col.header}
                      {sortable && (
                        <img
                          src={SortIcon}
                          alt=""
                          aria-hidden="true"
                          style={{
                            width: 9,
                            height: 14,
                            opacity: sortConfig.key === col.accessor ? 1 : 0.5,
                            transform:
                              sortConfig.key === col.accessor
                                ? sortConfig.direction === "asc"
                                  ? "rotate(180deg)"
                                  : "none"
                                : "none",
                            marginLeft: 2,
                            transition: "transform 0.2s",
                            display: "inline-block",
                          }}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((row, idx) =>
                customRowRender ? (
                  customRowRender(row, idx)
                ) : (
                  <tr key={idx}>
                    {normalizedCols.map((col, cIdx) => {
                      return (
                        <td key={cIdx}>
                          {col.cell ? col.cell(row) : row[col.accessor]}
                        </td>
                      );
                    })}
                  </tr>
                )
              )
            ) : (
              <tr>
                <td
                  colSpan={normalizedCols.length}
                  className="text-center text-muted"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {data.length > 0 && paginationComponent()}
    </div>
  );
};

SimpleTable.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.array,
  data: PropTypes.array,
  rowsPerPage: PropTypes.number,
  headerActions: PropTypes.node,
  scrollable: PropTypes.bool,
  scrollHeight: PropTypes.string,
  customRowRender: PropTypes.func,
  showPagination: PropTypes.bool,
  renderPagination: PropTypes.func,
  pageNumber: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default SimpleTable;
